// Light a character carries (torch, lamp, the Light spell) and the vision it
// has (darkvision from the sheet), written onto its Roll20 token's dynamic
// lighting. One light per token — Roll20 draws one — so a new source
// replaces the old; an aura that sheds light (Twilight Sanctuary's dim 30 ft)
// lights the token while it runs unless a brighter source is carried.
// Timed lights run on the initiative clock like auras (600 turns = an hour).
import type { Character } from "./types";

export interface LightDef { key: string; name: string; bright: number; dim: number; rounds: number; match?: RegExp }
// gear: which item lit it, so the light can go out when the item does and a
// hand-set light is never overwritten by the gear sweep.
export interface ActiveLight { key: string; name: string; bright: number; dim: number; remaining: number | null; total?: number; since: string; gear?: string }

export const LIGHTS: LightDef[] = [
  { key: "torch", name: "Torch", bright: 20, dim: 20, rounds: 600 },
  { key: "candle", name: "Candle", bright: 5, dim: 5, rounds: 600 },
  { key: "lamp", name: "Lamp", bright: 15, dim: 30, rounds: 600 },
  { key: "hooded-lantern", name: "Hooded lantern", bright: 30, dim: 30, rounds: 600 },
  { key: "hooded-lantern-dim", name: "Hooded lantern (lowered)", bright: 0, dim: 5, rounds: 600 },
  { key: "bullseye-lantern", name: "Bullseye lantern", bright: 60, dim: 60, rounds: 600 },
  { key: "light", name: "Light (spell)", bright: 20, dim: 20, rounds: 600, match: /^light$/i },
  { key: "daylight", name: "Daylight (spell)", bright: 60, dim: 60, rounds: 600, match: /^daylight$/i },
  { key: "produce-flame", name: "Produce Flame", bright: 10, dim: 10, rounds: 100, match: /produce flame/i },
  { key: "continual-flame", name: "Continual Flame", bright: 20, dim: 20, rounds: 0, match: /continual flame/i },
  { key: "flame-blade", name: "Flame Blade", bright: 10, dim: 10, rounds: 100, match: /^flame blade$/i },
  { key: "faerie-fire", name: "Faerie Fire (on self)", bright: 0, dim: 10, rounds: 10, match: /faerie fire/i },
];

/** Auras that shed light while they run (radius = the aura's). */
export const AURA_LIGHT: Record<string, { bright: number; dim: number }> = {
  "twilight-sanctuary": { bright: 0, dim: 30 },
};

export const lightLabel = (l: ActiveLight) => l.remaining === null || l.remaining === undefined ? l.name : `${l.name} (${l.remaining})`;

export function startLight(def: LightDef): ActiveLight {
  const timed = def.rounds > 0;
  return { key: def.key, name: def.name, bright: def.bright, dim: def.dim, remaining: timed ? def.rounds : null, total: timed ? def.rounds : undefined, since: new Date().toISOString() };
}

/** What the token should emit: the carried light, else a lit aura, else nothing. */
export function tokenLight(c: Character): { bright: number; dim: number } | null {
  const carried = c.light as ActiveLight | undefined;
  if (carried) return { bright: carried.bright, dim: carried.dim };
  let best: { bright: number; dim: number } | null = null;
  for (const a of c.auras || []) {
    const l = AURA_LIGHT[a.key];
    if (!l) continue;
    if (!best || l.bright + l.dim > best.bright + best.dim) best = { bright: l.bright, dim: l.dim };
  }
  return best;
}

/** Darkvision (or any "…vision" with a range) from the sheet, for night vision. */
export function tokenNightVision(c: Character): { distance: number } | null {
  let best = 0;
  for (const v of c.vision || []) {
    const range = Number(v.range || 0);
    if (range > best && /vision/i.test(String(v.type || ""))) best = range;
  }
  return best > 0 ? { distance: best } : null;
}

/** The caster's turn began: a timed light burns down; at 0 it goes out. */
export function tickLight(c: Character): { light: ActiveLight | undefined; ended: ActiveLight | null } {
  const l = c.light as ActiveLight | undefined;
  if (!l || l.remaining === null || l.remaining === undefined) return { light: l, ended: null };
  const left = Number(l.remaining) - 1;
  if (left <= 0) return { light: undefined, ended: l };
  return { light: { ...l, remaining: left }, ended: null };
}
export function untickLight(c: Character): ActiveLight | undefined {
  const l = c.light as ActiveLight | undefined;
  if (!l || l.remaining === null || l.remaining === undefined) return l;
  const cap = Number(l.total || 0) || LIGHTS.find((d) => d.key === l.key)?.rounds || Infinity;
  return { ...l, remaining: Math.min(cap, Number(l.remaining) + 1) };
}
export function setLightRemaining(l: ActiveLight, remaining: number): ActiveLight | undefined {
  const n = Math.max(0, Math.round(remaining));
  if (n === 0) return undefined;
  return { ...l, remaining: n, total: Math.max(Number(l.total || 0), n) };
}

/** A chat card whose caption names a light spell ("Light (Edam)") → def + caster. */
export function lightFromCard(caption: string): { def: LightDef; caster: string } | null {
  const name = caption.replace(/\s*\([^()]*\)\s*$/, "").trim();
  const def = LIGHTS.find((d) => d.match && d.match.test(name));
  if (!def) return null;
  return { def, caster: (caption.match(/\(([^()]+)\)\s*$/)?.[1] || "").trim() };
}

// ---- light the character is actually carrying ----
// The sheet gives item NAMES and whether each is equipped, attuned or
// switched on; it does not give the text that would say "sheds bright light
// in a 20-foot radius". So known gear is matched by name, and anything the
// table does not know can be TAUGHT once and is remembered by name after
// that — which is the only honest answer for homebrew and for the long tail
// of magic items nobody has typed in.
import type { Layout } from "./types";

export interface GearLight { key: string; name: string; bright: number; dim: number; rounds: number; match: RegExp }
/** Gear that sheds light, by the name D&D Beyond shows on the inventory row.
 *  rounds 0 = it does not burn down (magical, or an oil lamp nobody tracks). */
export const ITEM_LIGHTS: GearLight[] = [
  { key: "gear-torch", name: "Torch", bright: 20, dim: 20, rounds: 600, match: /^torch$/i },
  { key: "gear-everburning", name: "Everburning Torch", bright: 20, dim: 20, rounds: 0, match: /everburning torch/i },
  { key: "gear-candle", name: "Candle", bright: 5, dim: 5, rounds: 600, match: /^candle/i },
  { key: "gear-lamp", name: "Lamp", bright: 15, dim: 30, rounds: 600, match: /^lamp$/i },
  { key: "gear-hooded", name: "Hooded lantern", bright: 30, dim: 30, rounds: 600, match: /hooded lantern/i },
  { key: "gear-bullseye", name: "Bullseye lantern", bright: 60, dim: 60, rounds: 600, match: /bullseye lantern/i },
  { key: "gear-revealing", name: "Lantern of Revealing", bright: 30, dim: 30, rounds: 0, match: /lantern of revealing/i },
  { key: "gear-driftglobe", name: "Driftglobe", bright: 20, dim: 20, rounds: 0, match: /driftglobe/i },
  { key: "gear-moontouched", name: "Moon-Touched blade", bright: 0, dim: 15, rounds: 0, match: /moon.?touched/i },
  { key: "gear-sunblade", name: "Sun Blade", bright: 15, dim: 15, rounds: 0, match: /sun blade/i },
  { key: "gear-staff-of-light", name: "Staff of Light", bright: 20, dim: 20, rounds: 0, match: /staff of light\b/i },
  { key: "gear-gem-brightness", name: "Gem of Brightness", bright: 30, dim: 30, rounds: 0, match: /gem of brightness/i },
  { key: "gear-wand-light", name: "Wand of Light", bright: 20, dim: 20, rounds: 0, match: /wand of light\b/i },
  { key: "gear-ring-shooting-stars", name: "Ring of Shooting Stars", bright: 0, dim: 30, rounds: 0, match: /ring of shooting stars/i },
];

const norm = (v: string) => String(v || "").replace(/\s+/g, " ").trim().toLowerCase();
const TAUGHT = "itemLights";
export interface TaughtLight { name: string; bright: number; dim: number; rounds: number }
/** Items the DM has told the table about, by lower-cased item name. */
export const taughtLights = (layout: Layout): Record<string, TaughtLight> => (layout[TAUGHT] as Record<string, TaughtLight>) || {};
export function withTaughtLight(layout: Layout, itemName: string, light: TaughtLight | null): Layout {
  const all = { ...taughtLights(layout) };
  if (light) all[norm(itemName)] = light; else delete all[norm(itemName)];
  return { ...layout, [TAUGHT]: all };
}

export interface CarriedItem { item: string; bright: number; dim: number; rounds: number; key: string; label: string; on: boolean; known: boolean }
/** Everything on this character that could light the token, in one list:
 *  equipped gear, magic items and plain inventory. `on` is whether the sheet
 *  says it is in use right now (switched on, attuned, or equipped), which is
 *  what the automatic sweep follows. */
export function carriedLights(c: Character, layout: Layout): CarriedItem[] {
  const taught = taughtLights(layout);
  const rows: { name: string; on: boolean }[] = [];
  const add = (name: unknown, on: boolean) => { const n = String(name || "").trim(); if (n) rows.push({ name: n, on }); };
  for (const i of (c.equippedItems as { name?: string }[] | undefined) || []) add(i?.name, true);
  for (const i of (c.magicItems as { name?: string; active?: boolean; attuned?: boolean }[] | undefined) || []) add(i?.name, Boolean(i?.active || i?.attuned));
  for (const i of (c.inventoryItems as { name?: string; equipped?: boolean }[] | undefined) || []) add(i?.name, Boolean(i?.equipped));
  const out = new Map<string, CarriedItem>();
  for (const r of rows) {
    const key = norm(r.name);
    const known = ITEM_LIGHTS.find((g) => g.match.test(r.name));
    const mine = taught[key];
    if (!known && !mine) continue;
    const was = out.get(key);
    const entry: CarriedItem = mine
      ? { item: r.name, bright: mine.bright, dim: mine.dim, rounds: mine.rounds, key: `taught:${key}`, label: mine.name || r.name, on: r.on || Boolean(was?.on), known: false }
      : { item: r.name, bright: known!.bright, dim: known!.dim, rounds: known!.rounds, key: known!.key, label: known!.name, on: r.on || Boolean(was?.on), known: true };
    out.set(key, entry);
  }
  return [...out.values()];
}
/** Every carried item, lit or not, for the "teach the table what this is" picker. */
export function carriedItemNames(c: Character): string[] {
  const names = new Set<string>();
  for (const i of (c.equippedItems as { name?: string }[] | undefined) || []) if (i?.name) names.add(i.name);
  for (const i of (c.magicItems as { name?: string }[] | undefined) || []) if (i?.name) names.add(i.name);
  for (const i of (c.inventoryItems as { name?: string }[] | undefined) || []) if (i?.name) names.add(i.name);
  return [...names].sort((a, b) => a.localeCompare(b));
}
/** The brightest thing the character has switched on; null when nothing is. */
export function gearLight(c: Character, layout: Layout): ActiveLight | null {
  const lit = carriedLights(c, layout).filter((x) => x.on);
  if (!lit.length) return null;
  const best = lit.slice().sort((a, b) => (b.bright + b.dim) - (a.bright + a.dim))[0];
  return { key: best.key, name: best.label, bright: best.bright, dim: best.dim, remaining: best.rounds > 0 ? best.rounds : null, total: best.rounds > 0 ? best.rounds : undefined, since: new Date().toISOString(), gear: best.item };
}
/** Light carried gear by hand (the picker), as an ActiveLight. */
export const lightFromGear = (g: CarriedItem): ActiveLight => ({ key: g.key, name: g.label, bright: g.bright, dim: g.dim, remaining: g.rounds > 0 ? g.rounds : null, total: g.rounds > 0 ? g.rounds : undefined, since: new Date().toISOString(), gear: g.item });

// ---- putting a gear light OUT by hand, and keeping it out ----
// The automatic sweep lights a token from the gear switched on in the sheet, so
// putting a glowing item's light out from the card used to be undone on the very
// next tick: the item is still equipped, so the sweep re-lit it (reported from a
// live table 2026-10-11 — the × on a "Chain Shirt" light chip did nothing, and
// the feed showed "lights Chain Shirt" and "put out" alternating). The douse
// ledger is the sweep's memory of a light the DM meant to be out.

/** The ledger identity of a lit gear source — which item, and which light def.
 *  Two word-parts so a re-teach that changes the def (same item, new key) is a
 *  different source and relights, while the same equipped item stays doused. */
export const gearDouseKey = (l: { key?: string; gear?: string }) => `${l.gear || ""}|${l.key || ""}`;

export interface GearSweepDecision {
  /** Change the character's carried light this tick? */
  relight: boolean;
  /** The light to set when `relight` is true; `undefined` means put it out. */
  light?: ActiveLight;
  /** The douse marker as it should stand AFTER this tick (null = cleared). */
  doused: string | null;
}
/** What the automatic gear sweep should do for ONE character this tick.
 *
 *  Pure so the sweep's judgement can be tested without a browser (the sweep
 *  itself is a React effect in components/Shell.tsx). Inputs:
 *    have   — the light the character carries now (hand-set, gear-set, or none)
 *    want   — the brightest gear switched on in the sheet right now, or null
 *    doused — the gearDouseKey the DM put out by hand, or null
 *
 *  The cases the body must cover:
 *    • a HAND-SET light (`have && !have.gear`) is never touched by the sweep,
 *      and a hand-set light supersedes any douse (clear it).
 *    • the DM doused the gear that is STILL wanted (`want` and
 *      `doused === gearDouseKey(want)`): keep it out — put out any gear light
 *      that crept back, and keep the marker.
 *    • otherwise the marker, if any, is stale (the lit gear changed or is gone),
 *      so clear it and fall back to the ordinary sweep: nothing wanted and
 *      nothing carried is a no-op; the same gear already carried is a no-op;
 *      anything else relights to `want` (or puts it out when `want` is null).
 */
export function gearSweepDecision(have: ActiveLight | undefined, want: ActiveLight | null, doused: string | null): GearSweepDecision {
  // A hand-set light is the DM's, not the sweep's; it also makes any marker moot.
  if (have && !have.gear) return { relight: false, doused: null };
  // The DM put THIS gear out and it is still the lit source: keep it dark, keep
  // the marker. Put out a gear light that somehow crept back on in the meantime.
  if (want && doused === gearDouseKey(want)) return { relight: Boolean(have), light: undefined, doused };
  // Any other marker is stale — the lit gear changed or is gone — so forget it,
  // then the ordinary sweep: nothing on either side is a no-op; the same gear
  // already carried is a no-op; anything else relights to `want` (and a null
  // `want`, the item put away, puts the carried gear light out).
  if (!want && !have) return { relight: false, doused: null };
  if (want && have && have.key === want.key && have.gear === want.gear) return { relight: false, doused: null };
  return { relight: true, light: want || undefined, doused: null };
}
